import {useState, useRef, useEffect, useContext, useMemo} from "react"
import {useRouteLoaderData} from "react-router-dom"
import {DataTable} from "primereact/datatable"
import {Column} from "primereact/column"
import {default as cx} from "classnames"
import {Dropdown} from "primereact/dropdown"
import {MultiSelect} from "primereact/multiselect"
import {Skeleton} from "primereact/skeleton"
import Avatar from "react-avatar"
import Button from "react-bootstrap/Button"
import Modal from "react-bootstrap/Modal"
import useSWR from "swr"

import {fetcherGet, formatDateTime, getLoadingRows} from "../../utilities/general"
import {FiltersTable} from "../Layout"
import SubHeader from "../SubHeader.jsx"
import {getToken} from "../../utilities/useToken"
import RoleTabs, {getVisibleTabs, canAddUsers, canOnboardOrientees} from "./RolesTab"
import {useStableNavigate} from "../../utilities/StableNavigateContext"
import {readinessMapping} from "../Columns"
import UploadButton from "../UploadButton"
import {wrappedGet} from "../../utilities/AxiosHelper"
import RefContext from "../../utilities/RefContext"

const USER_STATUS_OPTIONS = [
    {label: "Enabled", value: "enabled"},
    {label: "Disabled", value: "disabled"}
]

const BASE_COLUMNS = [
    {header: "User Name", field: "user_name", export: true},
    {header: "Facility", field: "facility_name", export: true},
    {header: "Unit(s)", field: "units", export: true},
    {header: "Last Log In", field: "last_login", export: true},
]

const TAB_STORAGE_KEY = "usersRoleTab"
const EMPTY_ROWS = []
const LOADING_ROWS = getLoadingRows(4)

const UnitsCell = ({units}) => {
    if (!units || units.length === 0) return ""
    const [primary, ...rest] = [...units].sort((a, b) => a.localeCompare(b))
    return (
        <div className="d-flex align-items-center gap-2">
            <span>{primary}</span>
            {rest.length > 0 && (
                <span className="badge badge-light fw-semibold" title={rest.join(", ")}>
                    +{rest.length} more
                </span>
            )}
        </div>
    )
}

/* /users and /get-orientees return different shapes.
   Normalize both so the table renders from one set of field names. */
const normalizeUser = (row) => {
    if (row.loading) return row
    const hasName = row.first_name !== "" && row.last_name !== ""
    const user_name = hasName ? `${row.first_name} ${row.last_name}` : row.account
    return {
        id: row.id,
        loading: false,
        role_name: row.role_name,
        user_name,
        email: row.email,
        facility_name: row.facility_name,
        organization_name: row.organization_name,
        units: row.units?.map((u) => u.name) ?? [],
        last_login: row.last_login,
        active: row.active === true,
        organization_id: row.organization_id,
        facility_id: row.facility_id,
        unit_ids: row.units?.map((u) => u.id).filter(Boolean) ?? [],
        user_type_id: row.user_type_id,
    }
}

const normalizeEnterpriseAdmin = (row) => {
    if (row.loading) return row
    const hasName = row.first_name !== "" && row.last_name !== ""
    const user_name = hasName ? `${row.first_name} ${row.last_name}` : row.account
    return {
        id: row.id,
        loading: false,
        role_name: "Enterprise Admin",
        user_name,
        email: row.email,
        facility_name: "N/A",
        units: ["N/A"],
        last_login: row.last_login,
        active: row.active === true,
        organization_id: row.organization_id,
        unit_ids: [],
    }
}

const Users = () => {
    const {readOnly} = useRouteLoaderData("admin-users") ?? {readOnly: "true"}
    const {role} = getToken() || {}
    const navigate = useStableNavigate()
    const tableRef = useRef(null)
    const {dispatch} = useContext(RefContext)

    const visibleTabs = getVisibleTabs(role)
    const [tab, setTab] = useState(() => {
        const savedTab = localStorage.getItem(TAB_STORAGE_KEY)
        return visibleTabs.includes(savedTab) ? savedTab : visibleTabs[0]
    })

    const isOrientee = tab === "Orientee"
    const isEnterpriseAdmin = tab === "Enterprise Admin"

    const [rows, setRows] = useState(LOADING_ROWS)
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyState, setLazyState] = useState({
        first: 0,
        rows: 25,
        page: 0,
    });

    const [searchText, setSearchText] = useState("")
    // Settled search value; only updates after the user stops typing.
    const [debouncedSearch, setDebouncedSearch] = useState("")
    const [selectedUserStatus, setSelectedUserStatus] = useState("enabled")
    const [orgFilter, setOrgFilter] = useState(null)
    const [facilityFilter, setFacilityFilter] = useState(null)
    const [unitFilter, setUnitFilter] = useState(undefined)
    const [positionFilter, setPositionFilter] = useState(undefined)
    const [readinessFilter, setReadinessFilter] = useState(undefined)
    const [statusFilter, setStatusFilter] = useState(undefined)
    const [apiResultData, setApiResultData] = useState([])
    const [showApiResultModal, setShowApiResultModal] = useState(false)
    const [apiResultModalTitle, setApiResultModalTitle] = useState("")

    const {data: organizations} = useSWR("/get-organizations", fetcherGet, {fallbackData: EMPTY_ROWS})
    const {data: facilities} = useSWR(orgFilter ? `/organization/${orgFilter}/facility` : null, fetcherGet, {
        fallbackData: EMPTY_ROWS,
    })
    const {data: units} = useSWR(facilityFilter ? `/organization/${orgFilter}/facility/${facilityFilter}/unit` : null, fetcherGet, {
        fallbackData: EMPTY_ROWS,
    })

    useEffect(() => {
        document.title = "Users"
    }, [])

    // Debounce the search box so the settled value participates in the single fetch below.
    useEffect(() => {
        const timeoutId = setTimeout(() => setDebouncedSearch(searchText), 500)
        return () => clearTimeout(timeoutId)
    }, [searchText])

    // Single source of fetching: any settled tab/search/filter change resets to
    // page 0 and issues one combined request, so search and filters stay in sync.
    useEffect(() => {
        setLazyState((prev) => ({...prev, first: 0, page: 0}))
        fetchUsers(0, lazyState.rows)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        tab,
        debouncedSearch,
        selectedUserStatus,
        orgFilter,
        facilityFilter,
        unitFilter,
        positionFilter,
        readinessFilter,
        statusFilter,
    ])

    const fetchUsers = (page = 0, rows = 25) => {
        // PrimeReact pages are 0-based; the API expects 1-based.
        const apiPage = page + 1
        let url = `/users?page=${apiPage}&page_size=${rows}&role_name=${encodeURIComponent(tab)}`

        let normalize = normalizeUser
        if (isEnterpriseAdmin) {
            url = `/enterprise-admins?page=${apiPage}&page_size=${rows}`
            normalize = normalizeEnterpriseAdmin
        }

        if (debouncedSearch) {
            url = `${url}&search=${encodeURIComponent(debouncedSearch)}`
        }

        if (selectedUserStatus === "enabled") {
            url = `${url}&active=true`
        } else if (selectedUserStatus === "disabled") {
            url = `${url}&active=false`
        } else {
            url = `${url}&active=all`
        }

        if (orgFilter) {
            url = `${url}&org_id=${encodeURIComponent(orgFilter)}`
        }
        if (facilityFilter) {
            url = `${url}&facility_id=${encodeURIComponent(facilityFilter)}`
        }

        if (unitFilter && unitFilter.length > 0) {
            url = `${url}&unit_id=${encodeURIComponent(unitFilter)}`
        }

        if (positionFilter && positionFilter.length > 0) {
            url = `${url}&position_ids=${encodeURIComponent(positionFilter)}`
        }

        if (readinessFilter && readinessFilter.length > 0) {
            url = `${url}&readiness_category=${encodeURIComponent(readinessFilter)}`
        }

        if (statusFilter && statusFilter.length > 0) {
            url = `${url}&orientation_status_ids=${encodeURIComponent(statusFilter)}`
        }

        setRows(LOADING_ROWS)
        return wrappedGet(url)
            .then((response) => {
                setRows((response?.data?.results ?? []).map(normalize))
                if (response.data.pagination.total_records) {
                    setTotalRecords(response.data.pagination.total_records);
                }
            })
            .catch((error) => {
                setRows(EMPTY_ROWS)
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Users",
                    timeout: 5000,
                })
            })
    }

    const columns = BASE_COLUMNS

    const unitOptions = useMemo(() => {
        const unitMap = units.map((unit) => {
            return {value: unit.id, label: unit.name}
        })

        return unitMap
    }, [units]);

    const addUser = () => navigate("/admin/users/new")
    const onboardNewHire = () => navigate("/admin/new-hire-onboarding")

    const handleExport = () => {
        const exportRows = rows

        const csvRows = [
            columns.map((col) => col.header),
            ...exportRows.map((row) =>
                columns.map((col) => {
                    const value = getExportableField(col, row) ?? ""

                    return `"${String(value).replace(/"/g, '""')}"`
                })
            ),
        ]

        const csvContent = csvRows.map((row) => row.join(",")).join("\n")

        const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"})
        const url = URL.createObjectURL(blob)

        const link = document.createElement("a")
        link.href = url
        link.download = `${tab.toLowerCase().replace(/\s+/g, "-")}-users.csv`

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        URL.revokeObjectURL(url)
    }

    const changeTab = (newTab) => {
        localStorage.setItem(TAB_STORAGE_KEY, newTab)
        setTab(newTab)
    }

    const handleRowClick = (event) => {
        if (event.data.loading || readOnly) return
        // ID used for users, orientation_id used for Orientees for multiple orientations
        const {id, orientation_id} = event.data
        navigate(`/admin/users/edit/${id}${isOrientee ? `?orientation=${orientation_id}` : ""}`)
    }

    const getCellCustom = (col, row) => {
        if (!row) return ""
        if (row.loading) return <Skeleton className="w-100 h-40px" />

        const {field} = col
        if (field === "user_name") {
            return (
                <div className="d-flex align-items-center">
                    <Avatar name={row.user_name} size="40" round={true} className="me-3" maxInitials={2} />
                    <div className="d-flex flex-column">
                        <span className="fw-bold mb-1">{row.user_name}</span>
                        {row.email && <span>{row.email}</span>}
                    </div>
                </div>
            )
        }
        if (field === "units") return <UnitsCell units={row.units} />
        if (field === "readiness") {
            const mapped = readinessMapping[row.readiness]
            return mapped ? <div className={cx("badge px-4 py-3", mapped.badge)}>{mapped.text}</div> : ""
        }
        if (field === "last_login") {
            return formatDateTime(row.last_login)
        }
        return row[field] || ""
    }

    const getExportableField = (col, row) => {
        if (!row) return ""
        if (col.field === "units") return row.units?.join(", ") ?? ""
        return row[col.field]
    }

    const handleReset = () => {
        setSearchText("")
        setSelectedUserStatus("")
        setOrgFilter(null)
        setFacilityFilter(null)
        setUnitFilter(undefined)
        setPositionFilter(undefined)
        setReadinessFilter(undefined)
        setStatusFilter(undefined)
    }

    if (visibleTabs.length === 0) {
        return (
            <SubHeader title1="OARS" title2="Users" icon="fa-users-cog">
                <div className="text-muted">You do not have access to user accounts.</div>
            </SubHeader>
        )
    }

    const fetchUserTypes = () => {
        return wrappedGet("get-types")
            .then((response) => {
                if (response.data && response.data.results.length > 0) {
                    setApiResultData(response.data.results)
                    setApiResultModalTitle("Types")
                    setShowApiResultModal(true)
                }
            })
            .catch((error) => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching User Types",
                    timeout: 5000,
                })
            })
    }

    const fetchUserRoles = () => {
        return wrappedGet("get-roles")
            .then((response) => {
                if (response.data && response.data.results.length > 0) {
                    setApiResultData(response.data.results)
                    setApiResultModalTitle("Roles")
                    setShowApiResultModal(true)
                }
            })
            .catch((error) => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching User Roles",
                    timeout: 5000,
                })
            })
    }

    const fetchOrganizations = () => {
        return wrappedGet("get-organizations")
            .then((response) => {
                if (response.data && response.data.results.length > 0) {
                    setApiResultData(response.data.results)
                    setApiResultModalTitle("Organization")
                    setShowApiResultModal(true)
                }
            })
            .catch((error) => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching User Organization",
                    timeout: 5000,
                })
            })
    }

    const handleCloseApiResultModal = () => {
        setShowApiResultModal(false)
    }

    const onPage = (event) => {
        setLazyState(event);

        fetchUsers(
            event.page,
            event.rows,
        );
    };

    const helpContent = (
        <>
            <h4>Instructions</h4>
            <p>Bulk uploading Users requires a CSV file with the following fields</p>

            <ol>
                <li>
                    <strong>organization_id:</strong> The id of the unit you are in.{" "}
                    <button
                        className="btn btn-link fs-7 p-0"
                        onClick={(e) => {
                            e.preventDefault()
                            fetchOrganizations()
                        }}
                    >
                        Get my organization id
                    </button>{" "}
                    This will be ignored for an edit.
                </li>
                <li>
                    <strong>email:</strong> Email address
                </li>
                <li>
                    <strong>first_name:</strong> First Name
                </li>
                <li>
                    <strong>last_name:</strong> Last Name
                </li>
                <li>
                    <strong>account:</strong> When using Active Directory this is the value that will be passed to SSO.
                    This will be ignored for an edit. If you would like to change this please contact support.
                </li>
                <li>
                    <strong>type_id:</strong> See current values{" "}
                    <button
                        className="btn btn-link fs-7 p-0"
                        onClick={(e) => {
                            e.preventDefault()
                            fetchUserTypes()
                        }}
                    >
                        here
                    </button>
                </li>
                <li>
                    <strong>role_id:</strong> See current values{" "}
                    <button
                        className="btn btn-link fs-7 p-0"
                        onClick={(e) => {
                            e.preventDefault()
                            fetchUserRoles()
                        }}
                    >
                        here
                    </button>
                </li>
                <li>
                    <strong>active:</strong> not required but can be specified as a boolean as either 'TRUE' or 'FALSE'
                </li>
                <li>
                    <strong>employee_id:</strong> A unique identifier that links them to your HR system. This will be
                    ignored for an edit. If you would like to change this please contact support.
                </li>
                <li>
                    <strong>phone_number:</strong> Employee's phone number, can be work or home
                </li>
                <li>
                    <strong>allow_osrs:</strong> Allow OSR submission. Boolean (<code>TRUE</code>/<code>FALSE</code>)
                    that enables this user to create OSRs. Defaults to <code>FALSE</code> when blank.
                </li>
                <li>
                    <strong>facility_id:</strong> Optional. The id of the facility to associate the user with. Required
                    when unit_id is provided.
                </li>
                <li>
                    <strong>unit_id:</strong> Optional. One or more unit ids within the facility above. Separate
                    multiple units with a pipe, e.g. <code>111|222|333</code>. All units must belong to facility_id.
                </li>
                {role === "Enterprise Admin" && (
                    <li>
                        <strong>overwrite_units:</strong> Optional boolean (<code>TRUE</code>/<code>FALSE</code>), for
                        enterprise admins only. When <code>TRUE</code>, the user's existing units within the given
                        facility are replaced with the units listed in this row. When blank or <code>FALSE</code>, the
                        listed units are added and existing units are kept.
                    </li>
                )}
            </ol>
            <h5>Example CSV File</h5>
            <pre>
                organization_id,email,first_name,last_name,account,type_id,role_id,active,employee_id,phone_number,allow_osrs,facility_id,unit_id,overwrite_units
                <br />
                99999999-9999-9999-9999-999999999999,test@example.com,Test,Person,test.person,1,2,TRUE,1234,555-123-1234,FALSE,10,111|222|333,FALSE
            </pre>
            <Modal show={showApiResultModal} onHide={handleCloseApiResultModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{apiResultModalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <table className="table align-middle table-rounded table-row-bordered border gs-4 my-0 fs-6 w-100">
                        <thead className="text-start text-light text-uppercase bg-gray-700 align-middle fs-7 fw-bold">
                            <tr>
                                <td>id</td>
                                <td>Name</td>
                            </tr>
                        </thead>
                        <tbody>
                            {apiResultData.map((type) => {
                                return (
                                    <tr key={`api-data-${type.id}`}>
                                        <td>{type.id}</td>
                                        <td>{type.name}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleCloseApiResultModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )

    return (
        <>
            <SubHeader title1="OARS" title2="Users" icon="fa-users-cog">
                <div className="row row-cols-2 row-cols-sm-auto g-2 flex-grow-1 justify-content-end">
                    <div className="col">
                        <button type="button" className="btn btn-light-primary w-100" onClick={() => handleExport()}>
                            <i className="ki-outline ki-exit-up fs-2"></i>Export
                        </button>
                    </div>
                    {canAddUsers(role) && !readOnly && (
                        <>
                            <div className="col">
                                <button
                                    type="button"
                                    className="btn btn-light-primary fw-semibold w-100"
                                    onClick={() => addUser()}
                                >
                                    <i className="fas fa-plus"></i>Add User Account
                                </button>
                            </div>
                            <div className="col">
                                <UploadButton
                                    uploadUrl={`bulk/upload/users`}
                                    label="Bulk Load Users"
                                    dispatch={dispatch}
                                    reload={fetchUsers}
                                    helpTitle="Bulk Upload Users"
                                    helpContent={helpContent}
                                />
                            </div>
                        </>
                    )}
                    {canOnboardOrientees(role) && !readOnly && (
                        <div className="col">
                            <button
                                type="button"
                                className="btn btn-primary fw-semibold w-100"
                                onClick={() => onboardNewHire()}
                            >
                                <i className="fas fa-plus"></i>Start a New Orientation
                            </button>
                        </div>
                    )}
                </div>
            </SubHeader>
            <div className="container-fluid">
                <RoleTabs tabs={visibleTabs} active={tab} onChange={changeTab} />
            </div>
            <FiltersTable
                Filters={
                    <div className="row g-4 align-items-end">
                        <div className="col-12 col-sm-6 col-md-4 col-xl">
                            <label htmlFor="id-search-users" className="form-label fw-bold">
                                Search Users:
                            </label>
                            <div className="d-flex align-items-center position-relative">
                                <i className="ki-outline ki-magnifier fs-3 position-absolute ms-5"></i>
                                <input
                                    id="id-search-users"
                                    type="text"
                                    className="form-control form-control-solid ps-12"
                                    placeholder="Account / Name"
                                    value={searchText}
                                    onChange={(evt) => {
                                        setSearchText(evt.target.value)
                                    }}
                                />
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-4 col-xl">
                            <label htmlFor="id-search-users" className="form-label fw-bold">
                                User Status
                            </label>
                            <Dropdown
                                className="w-100 p-dropdown-tall"
                                showClear={Boolean(selectedUserStatus)}
                                options={USER_STATUS_OPTIONS}
                                filter={false}
                                value={selectedUserStatus}
                                placeholder="User Status"
                                onChange={(e) => setSelectedUserStatus(e.value ?? "")}
                            />
                        </div>
                        {role === "Enterprise Admin" && tab !== "Enterprise Admin" && (
                            <div className="col-12 col-sm-6 col-md-4 col-xl">
                                <label className="form-label fw-bold">Organization</label>
                                <Dropdown
                                    className="w-100 p-dropdown-tall"
                                    showClear={true}
                                    key={orgFilter}
                                    options={organizations.map((org) => ({label: org.name, value: org.id}))}
                                    filter={false}
                                    value={orgFilter}
                                    placeholder="Organization"
                                    onChange={(option) => {
                                        setOrgFilter(option ? option.value : null)
                                        setFacilityFilter(null)
                                        setUnitFilter(undefined)
                                        setPositionFilter(undefined)
                                    }}
                                />
                            </div>
                        )}
                        {!!orgFilter && (
                            <div className="col-12 col-sm-6 col-md-4 col-xl">
                                <label className="form-label fw-bold">Facility</label>
                                <Dropdown
                                    className="w-100 p-dropdown-tall"
                                    showClear={true}
                                    key={facilityFilter}
                                    options={facilities.map((f) => ({label: f.name, value: f.id}))}
                                    filter={false}
                                    value={facilityFilter}
                                    placeholder="Facility"
                                    onChange={(option) => {
                                        setFacilityFilter(option ? option.value : null)
                                        setUnitFilter(undefined)
                                    }}
                                />
                            </div>
                        )}
                        {!!orgFilter && !!facilityFilter && (
                        <div className="col-12 col-sm-6 col-md-4 col-xl">
                            <label htmlFor="id-unit" className="form-label fw-bold">
                                Unit
                            </label>
                            <MultiSelect
                                inputId="id-unit"
                                showClear
                                className="w-100 p-dropdown-tall p-dropdown p-component p-inputwrapper p-dropdown-clearable"
                                options={unitOptions}
                                filter={unitOptions.length > 10}
                                value={unitFilter}
                                placeholder="Select Option"
                                maxSelectedLabels={1}
                                onChange={(selected) => {
                                    setUnitFilter(selected.value)
                                }}
                            />
                        </div>
                        )}
                        <div className="col-12 col-sm-auto">
                            <div className="d-flex justify-content-start">
                                <button
                                    type="reset"
                                    className="btn btn-secondary fw-semibold px-6"
                                    onClick={handleReset}
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                }
                Table={
                    <div className="table-responsive">
                        <DataTable
                            ref={tableRef}
                            value={rows}
                            dataKey="id"
                            tableClassName="table gy-3 gs-5 rounded w-100 mt-3 align-middle"
                            lazy
                            paginator
                            first={lazyState.first}
                            rows={lazyState.rows}
                            totalRecords={totalRecords}
                            onPage={onPage}
                            sortField={columns[0].field}
                            sortOrder={1}
                            rowsPerPageOptions={[25, 50, 75, 100]}
                            rowHover={true}
                            rowClassName={cx({
                                "cursor-pointer": !readOnly,
                            })}
                            onRowClick={handleRowClick}
                            scrollable
                            scrollHeight="800px"
                        >
                            {columns.map((colDef) => (
                                <Column
                                    key={colDef.field}
                                    field={colDef.field}
                                    header={colDef.header}
                                    headerClassName="th-sorting-center align-middle text-start text-light text-uppercase bg-gray-700 fs-7 fw-bold p-sortable-column"
                                    sortable
                                    body={(row) => getCellCustom(colDef, row)}
                                    exportable={colDef.export}
                                    exportField={(row) => getExportableField(colDef, row)}
                                />
                            ))}
                        </DataTable>
                    </div>
                }
            />
        </>
    )
}

export default Users
